import React, { FC, useMemo } from 'react'
import { Box, Collapse, makeStyles, Typography } from '@material-ui/core'
import clsx from 'clsx'
import { filter, isEqual, map, sortBy, uniqBy } from 'lodash'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  DateRangeFilterPicker,
  MultipleFilterSelector,
  receivedPageStateVar,
  SearchField,
  uploadDocsPageStateVar,
} from '../../components'
import { useDetermineUserRights, usePageFiltersSorting } from '../../hooks'
import { SearchName } from '../../types'
import { documentTypesArray } from '../../utils'
import {
  additionalDocumentOptions,
  CreditTransactionDocumentOptions,
  DebitTransactionDocumentOptions,
  TransactionDocumentListBusiness,
} from '../../utils/Data'
import { LimitedAccessRight } from '../../graphql'
import { PATH_PARAMS } from '../../routes/paths'
import { useParams } from 'react-router-dom'

const useStyles = makeStyles((theme) => ({
  filterBox: {
    width: '100%',
    display: 'flex',
    gap: theme.spacing(2),
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: theme.spacing(108),
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      maxWidth: '100%',
    },
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1.5),
    },
  },
  searchField: {
    minWidth: theme.spacing(30),
    '& label': {
      paddingRight: theme.spacing(2.5),
    },
    [theme.breakpoints.down('md')]: {
      minWidth: '100%',
      paddingRight: 0,
    },
  },
  searchWidth: {
    flex: 1,
  },
  filterButton: {
    flex: 1,
    height: '48px',
    minWidth: '188px',
    [theme.breakpoints.down('md')]: {
      height: 'unset',
      paddingRight: 0,
      '&.docType': {
        margin: 0,
      },
    },
    [theme.breakpoints.down('xs')]: {
      '&.docType': {
        width: '100%',
        paddingRight: 0,
        margin: 0,
      },
    },
    [theme.breakpoints.down('xs')]: {
      flex: 'unset',
      width: '100%',
    },
  },
  picker: {
    minWidth: '250px',
    paddingRight: theme.spacing(0),
    [theme.breakpoints.down('xs')]: {
      margin: 0,
    },
  },
  listLabel: {
    fontWeight: 'bold',
  },
  listWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'flex-start',
    },
  },
}))

const DocumentFiltersComponent: FC<{
  uploadedTab: boolean
}> = ({ uploadedTab }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const { pageFilters, setPageFilters } = usePageFiltersSorting(
    uploadedTab ? uploadDocsPageStateVar : receivedPageStateVar,
  )
  const { documentType } = pageFilters

  const { [PATH_PARAMS.applicationId]: applicationId } = useParams() as Record<string, string>

  const tableElement = document.getElementById('documentTableId')

  const { userRights } = useDetermineUserRights(applicationId)

  const isViewOnly =
    userRights?.limitedAccessRight === LimitedAccessRight.ViewOnly ||
    userRights?.limitedAccessRight === LimitedAccessRight.ViewDataEntry

  const uploadedDocOptions = useMemo(() => {
    const options = [
      ...DebitTransactionDocumentOptions,
      ...CreditTransactionDocumentOptions,
      ...TransactionDocumentListBusiness,
      ...additionalDocumentOptions,
    ]

    const docOptions = uniqBy(options, 'key')

    return sortBy(docOptions, 'label')
  }, [])

  const receivedDocOptions = useMemo(() => {
    const optionsUploadedDoc = documentTypesArray.map(({ key, label }) => ({ key, label: label }))

    return sortBy(optionsUploadedDoc, 'label')
  }, [documentTypesArray])

  const optionsList = useMemo(() => (uploadedTab ? uploadedDocOptions : receivedDocOptions), [
    uploadedDocOptions,
    receivedDocOptions,
  ])
  const isTypeListShown = documentType.length > 1 && documentType.length !== optionsList.length

  const multipleTypes = useMemo(
    () =>
      map(
        filter(optionsList, ({ key }) => documentType.includes(key as never)),
        'label',
      ).join(', '),
    [optionsList, documentType],
  )

  return (
    <>
      <Box className={classes.filterBox}>
        <Box className={clsx(classes.searchField, isViewOnly && classes.searchWidth)}>
          <SearchField
            name={
              uploadedTab ? SearchName.SearchUploadDocuments : SearchName.SearchReceivedDocuments
            }
            setValue={(value) => setPageFilters({ searchValue: value, page: 1 })}
            currentValue={pageFilters.searchValue || ''}
            placeHolder={t('searchDocumentByName', 'Search document by name')}
          />
        </Box>

        {!isViewOnly && (
          <Box className={clsx(classes.filterButton, `docType`)}>
            <MultipleFilterSelector
              optionsList={optionsList}
              setValue={(value) => setPageFilters({ documentType: value as string[], page: 1 })}
              currentValue={documentType}
              defaultOptionLabel={t('allTypes', 'All types')}
              name={t('type', 'Type')}
            />
          </Box>
        )}

        <Box className={clsx(classes.filterButton, classes.picker)}>
          <DateRangeFilterPicker
            dateRange={pageFilters.period}
            setDateRange={(value) => setPageFilters({ period: value, page: 1 })}
          />
        </Box>
      </Box>

      {tableElement &&
        ReactDOM.createPortal(
          <Collapse in={isTypeListShown}>
            <Box mr={3} color="blue" my={1}>
              {isTypeListShown && (
                <Box className={classes.listWrapper}>
                  <Typography className={classes.listLabel}>
                    {t('selectedTypes', 'Selected types')}:
                  </Typography>
                  <Typography>{multipleTypes}</Typography>
                </Box>
              )}
            </Box>
          </Collapse>,
          tableElement,
        )}
    </>
  )
}

export const DocumentFilters = React.memo(DocumentFiltersComponent, isEqual)
